const BASE_URL = process.env.REACT_APP_BASE_URL || "";

// --- ApiError ---

export interface ApiErrorResponse {
  data: any;
  status: number;
  headers: Headers;
}

export class ApiError extends Error {
  readonly response: ApiErrorResponse | undefined;
  readonly request: Request | undefined;
  readonly config: { url: string; method: string; headers: Record<string, string>; data?: any };
  constructor(
    message: string,
    config: ApiError["config"],
    request?: Request,
    response?: ApiErrorResponse
  ) {
    super(message);
    this.name = "ApiError";
    this.config = config;
    this.request = request;
    this.response = response;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError || (error instanceof Error && error.name === "ApiError");
}

// 400 from the server = user-input rejection (validation, duplicate, etc.).
// These are surfaced to the user via toasts and aren't bugs worth reporting.
export function isValidationError(error: unknown): boolean {
  return isApiError(error) && error.response?.status === 400;
}

export function isNotFoundError(error: unknown): boolean {
  return isApiError(error) && error.response?.status === 404;
}

// 503 from the server = planned maintenance ("under maintenance"). Handled globally
// (see useMaintenance) to swap the whole UI for the maintenance page on any endpoint.
export function isServiceUnavailable(error: unknown): boolean {
  return isApiError(error) && error.response?.status === 503;
}

// --- Default Headers ---

const defaultHeaders = new Map<string, string>([
  ["Content-Type", "application/json"],
]);

export function setDefaultHeader(key: string, value: string): void {
  defaultHeaders.set(key, value);
}

export function deleteDefaultHeader(key: string): void {
  defaultHeaders.delete(key);
}

// --- Interceptors ---

type InterceptorSuccessFn = (response: any) => any;
type InterceptorErrorFn = (error: any) => any;

interface Interceptor {
  onFulfilled?: InterceptorSuccessFn;
  onRejected?: InterceptorErrorFn;
}

const interceptors: Interceptor[] = [];

export function addResponseInterceptor(
  onFulfilled?: InterceptorSuccessFn,
  onRejected?: InterceptorErrorFn
): () => void {
  const interceptor: Interceptor = { onFulfilled, onRejected };
  interceptors.push(interceptor);

  return () => {
    const index = interceptors.indexOf(interceptor);
    if (index !== -1) interceptors.splice(index, 1);
  };
}

// --- Request Config ---

export interface RequestConfig {
  headers?: Record<string, string>;
  data?: any;
  signal?: AbortSignal;
  /** Extra properties forwarded to ApiError.config (e.g. _retry flag) */
  [key: string]: any;
}

// --- Core Request ---

async function request<T>(
  method: string,
  url: string,
  data?: any,
  config?: RequestConfig
): Promise<T> {
  const headers = new Headers();

  // Apply default headers
  defaultHeaders.forEach((value, key) => headers.set(key, value));

  // Apply per-request headers
  if (config?.headers) {
    Object.entries(config.headers).forEach(([key, value]) => headers.set(key, value));
  }

  // Build body
  let body: BodyInit | undefined;
  if (data instanceof FormData) {
    headers.delete("Content-Type"); // let browser set boundary
    body = data;
  } else if (typeof data === "string") {
    body = data;
  } else if (data !== undefined && data !== null) {
    body = JSON.stringify(data);
  }

  const fullUrl = url.startsWith("http") ? url : `${BASE_URL}${url}`;

  const { headers: _h, data: _d, signal: _s, ...extraConfig } = config || {};
  const configObj: ApiError["config"] & Record<string, any> = {
    ...extraConfig,
    url: fullUrl,
    method,
    headers: (() => { const h: Record<string, string> = {}; headers.forEach((v, k) => { h[k] = v; }); return h; })(),
    data,
  };

  let req: Request | undefined;
  let response: Response;

  try {
    req = new Request(fullUrl, {
      method,
      headers,
      body,
      signal: config?.signal,
    });
    response = await fetch(req);
  } catch (networkError: any) {
    const err = new ApiError(
      networkError?.message || "Network error",
      configObj,
      req,
      undefined
    );
    const interceptorResult = await runErrorInterceptors(err);
    if (interceptorResult.recovered) {
      return interceptorResult.value as T;
    }
    throw interceptorResult.value;
  }

  // Parse response body
  const contentType = response.headers.get("content-type");
  let responseData: any;
  try {
    if (contentType?.includes("application/json")) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }
  } catch {
    responseData = null;
  }

  if (!response.ok) {
    const err = new ApiError(
      `Request failed with status ${response.status}`,
      configObj,
      req,
      { data: responseData, status: response.status, headers: response.headers }
    );
    const interceptorResult = await runErrorInterceptors(err);
    if (interceptorResult.recovered) {
      return interceptorResult.value as T;
    }
    throw interceptorResult.value;
  }

  // Run success interceptors
  let result = responseData;
  for (const interceptor of interceptors) {
    if (interceptor.onFulfilled) {
      result = interceptor.onFulfilled(result);
    }
  }

  return result as T;
}

interface InterceptorResult {
  recovered: boolean;
  value: any;
}

async function runErrorInterceptors(error: any): Promise<InterceptorResult> {
  let currentError = error;
  for (const interceptor of interceptors) {
    if (interceptor.onRejected) {
      try {
        const result = await interceptor.onRejected(currentError);
        // Interceptor resolved (e.g. token refresh + retry succeeded) — recover
        return { recovered: true, value: result };
      } catch (e) {
        currentError = e;
      }
    }
  }
  // All interceptors re-threw — still an error
  return { recovered: false, value: currentError };
}

// --- Retry (used by useConnection for token refresh) ---

export async function retryRequest<T>(config: ApiError["config"] & Record<string, any>): Promise<T> {
  const { url, method, data, headers, ...extra } = config;
  return request<T>(method, url, data, { headers, ...extra });
}

// --- API Client ---

export const apiClient = {
  get<T>(url: string, config?: RequestConfig): Promise<T> {
    return request<T>("GET", url, undefined, config);
  },
  post<T>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return request<T>("POST", url, data, config);
  },
  patch<T>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return request<T>("PATCH", url, data, config);
  },
  put<T>(url: string, data?: any, config?: RequestConfig): Promise<T> {
    return request<T>("PUT", url, data, config);
  },
  delete<T>(url: string, config?: RequestConfig): Promise<T> {
    return request<T>("DELETE", url, config?.data, config);
  },
};
